import { supabase } from '../../supabase';
import { isNetworkError, isOnline, notifyOfflineStatus } from './network';
import {
  countPendingOutbox,
  listPendingOutbox,
  markOutboxDone,
  markOutboxFailed,
  markOutboxSyncing,
  resetFailedOutbox,
} from './outbox';
import { executeGenericMutation } from './runMutation';
import { isOfflineInvoiceId } from './ids';

let started = false;
let draining = false;
let timer = null;

async function existingPayment(payload) {
  if (!payload?.gym_id) return null;
  if (payload.payment_ref) {
    const { data } = await supabase
      .from('payments')
      .select('id, payment_ref, invoice_id')
      .eq('gym_id', payload.gym_id)
      .eq('payment_ref', payload.payment_ref)
      .maybeSingle();
    if (data) return data;
  }
  if (payload.id) {
    const { data } = await supabase
      .from('payments')
      .select('id, payment_ref, invoice_id')
      .eq('id', payload.id)
      .maybeSingle();
    if (data) return data;
  }
  return null;
}

async function existingById(table, id) {
  if (!id) return null;
  const { data } = await supabase.from(table).select('id').eq('id', id).maybeSingle();
  return data || null;
}

async function prepareInsertPayload(item) {
  const payload = { ...item.payload };
  if (item.table === 'payments' && isOfflineInvoiceId(payload.invoice_id)) {
    // Clear OFF- placeholder — DB BEFORE INSERT trigger assigns Rec NO in-txn
    payload.invoice_id = null;
  }
  return payload;
}

async function executeItem(item) {
  if (item.op === 'insert') {
    if (item.table === 'payments') {
      const dup = await existingPayment(item.payload);
      if (dup) return dup;
    } else if (item.payload?.id) {
      const dup = await existingById(item.table, item.payload.id);
      if (dup) return dup;
    }
    const prepared = { ...item, payload: await prepareInsertPayload(item) };
    if (item.source === 'receptionist-helper') {
      const { replayReceptionistMutation } = await import('../receptionist/receptionistApi');
      return replayReceptionistMutation(prepared);
    }
    return executeGenericMutation(prepared);
  }

  if (item.source === 'receptionist-helper') {
    const { replayReceptionistMutation } = await import('../receptionist/receptionistApi');
    return replayReceptionistMutation(item);
  }
  return executeGenericMutation(item);
}

export async function drainOutbox() {
  if (draining || !isOnline()) return { synced: 0, failed: 0 };
  draining = true;
  let synced = 0;
  let failed = 0;
  try {
    const items = await listPendingOutbox();
    for (const item of items) {
      if (!isOnline()) break;
      try {
        await markOutboxSyncing(item.id);
        const result = await executeItem(item);
        if (result?.error) throw result.error;
        await markOutboxDone(item.id);
        synced += 1;
      } catch (error) {
        if (isNetworkError(error)) {
          await markOutboxFailed(item.id, error, item.retries);
          break;
        }
        await markOutboxFailed(item.id, error, (item.retries || 0) + 1);
        failed += 1;
      }
    }
  } finally {
    draining = false;
    const pending = await countPendingOutbox();
    notifyOfflineStatus({ pending, synced, failed });
  }
  return { synced, failed };
}

export async function retryFailedOutbox() {
  await resetFailedOutbox();
  return drainOutbox();
}

export function startSyncEngine() {
  if (started || typeof window === 'undefined') return;
  started = true;

  const kick = () => {
    drainOutbox().catch(() => {});
  };

  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kick();
  });
  timer = window.setInterval(() => {
    if (isOnline()) kick();
  }, 25000);

  kick();
}

export function stopSyncEngine() {
  if (typeof window === 'undefined') return;
  started = false;
  if (timer) {
    window.clearInterval(timer);
    timer = null;
  }
}
